import { type AvailableIcons } from '$/components/common/Icon';
import { t } from '$/components/core/Localization/18n';
import { getMaterialSearchFilterByType } from '$/services/search/features/filter';
import { type MaterialFilterOption } from '$/services/usecases/materials';
import { type FilterType } from '$/services/usecases/materials/mapper/materials';

export type FilterOptions = Awaited<ReturnType<typeof getFilterOptions>>;
export type FilterOptionGroup = FilterOptions['general'][number];
export type FilterOptionItem = FilterOptionGroup['items'][number];

const searchNameToIcon = new Map<string, AvailableIcons>([
  ['producer', 'producer'],
  ['collection', 'collection'],
  ['colorRanges', 'color_palette'],
  ['productGroup', 'filter'],
  ['colorIntense', 'color_intensity'],
  ['design', 'design'],
  ['material', 'material'],
  ['rangeOfUse', 'field_of_use'],
]);

export const getFilterOptions = async (
  opt: {
    filter?: MaterialFilterOption[];
    type?: FilterType;
    disableAdminFilter?: boolean;
  },
  signal: AbortSignal,
) => {
  const filter = (await getMaterialSearchFilterByType(opt, signal)) ?? [];

  const transformed = filter.map((data) => {
    const items = data.items.map((item) => ({
      count: item.count,
      value: item.option,
      payload: item.payload,
      displayName: t(
        `materials.filter.item.${data.searchField}.${item.option}`,
      ),
    }));

    return {
      ...data,
      items: items,
      displayName: t(`materials.filter.group.${data.searchField}`),
      iconName: searchNameToIcon.get(data.searchField) ?? 'filter',
    };
  });

  return {
    general: transformed.filter((option) => !option.isAdditional),
    additional: transformed.filter((option) => option.isAdditional),
  };
};

export const getUnfilteredFilterOption = async (
  searchField: 'producer' | 'collection',
  signal: AbortSignal,
) => {
  const filter = await getFilterOptions(
    { type: 'all', disableAdminFilter: true },
    signal,
  );

  const producerFilter = [...filter.general, ...filter.additional].find(
    (option) => option.searchField === searchField,
  );

  return producerFilter?.items ?? [];
};

export const getAdminCollections = async (signal: AbortSignal) => {
  const result = await getMaterialSearchFilterByType(
    { type: 'admin_collections' },
    signal,
  );

  const combiStrings = result?.[0].items ?? [];

  const collections = combiStrings.map((item) => {
    const [producerId, collectionId] = (item.option as string).split('|');

    return {
      value: collectionId,
      payload: producerId,
      count: item.count,
      displayName: t(`materials.filter.item.collection.${collectionId}`),
    };
  });

  return collections;
};
